/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  useRef,
  useState,
  useEffect,
  type ComponentProps,
  type ElementType,
} from 'react';
import classnames from 'classnames';
import Collapsible from 'components/Collapsible/Collapsible';
import Picture from 'components/Picture/Picture';
import scrollTo from 'utils/scrollTo';
import useGuid from 'effects/useGuid';
import '../Ensemble.scss';
import './Actors.scss';

const DEFAULT_LIST: ActorsItem[] = [];

interface ActorsItem {
  id: string | number;
  url: string;
  image?: ComponentProps<typeof Picture>;
  name?: string;
  role?: string;
  period?: string;
  active?: boolean;
}

interface ActorsProps {
  id?: string | number | null;
  level?: number;
  heading?: string;
  previous?: string;
  list?: ActorsItem[];
  more?: string;
  container?: Element | null;
}

/**
 * This is the Actors component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Actors({
  id = null,
  level = 3,
  heading = 'Actors',
  previous: previousHeading = 'Previous actors',
  list = DEFAULT_LIST,
  more = 'Read more about «{title}»',
  container = null,
}: ActorsProps) {
  const guid = useGuid('ensemble-actors');
  const node = useRef<HTMLElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const Header = `h${level}` as ElementType;

  useEffect(() => {
    function onFocus() {
      if (!node.current!.contains(document.activeElement)) {
        setFocus(false);
      }
    }

    if (focus && open) {
      document.body.addEventListener('focusin', onFocus);
    }

    return () => {
      document.body.removeEventListener('focusin', onFocus);
    };
  }, [focus, open]);

  useEffect(() => {
    function onChange() {
      if (`#${id}` === window.location.hash) {
        setOpen(true);
      }
    }

    window.addEventListener('hashchange', onChange);
    onChange();

    return () => {
      window.removeEventListener('hashchange', onChange);
    };
  }, [id]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    function onOpen() {
      timeout = setTimeout(() => {
        setOpen(true);
      }, 1000);
    }

    if (container) {
      container.addEventListener('open', onOpen);
    }

    return function cleanup() {
      clearTimeout(timeout);
      if (container) {
        container.removeEventListener('open', onOpen);
      }
    };
  }, []);

  useEffect(() => {
    if (opened) {
      const showMenu = document.getElementById('show-sub-menu');
      if (showMenu) {
        scrollTo(node.current!, {
          alignTop: true,
          offset: showMenu.offsetHeight + 70,
        });
      } else {
        scrollTo(node.current!, { alignTop: true, offset: 180 });
      }
    }
  }, [opened]);

  const current = list.filter(({ active }) => active);
  const previous = list.filter(({ active }) => !active);

  function renderPeriod(period?: string) {
    if (!period) return null;

    return <div className="period">{period}</div>;
  }

  function renderPrevious() {
    if (previous.length === 0) return null;

    return (
      <div className="previous">
        <h4 className="h3">{previousHeading}</h4>
        <dl>
          {previous.map(({ id, url, name, role, period }) => {
            const moreTitle = more.replace('{title}', String(name));

            return (
              <div key={id}>
                <dt>{role}</dt>
                <dd>
                  <a
                    key={id}
                    href={url}
                    title={moreTitle}
                    aria-label={moreTitle}
                    tabIndex={open ? undefined : -1}
                  >
                    {name}
                  </a>
                  {renderPeriod(period)}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    );
  }

  return (
    <section
      ref={node}
      className={classnames('ensemble', 'actors', { open })}
      onFocus={() => {
        setFocus(true);
      }}
    >
      <button
        ref={button}
        id={`button${guid}`}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={`details${guid}`}
        onClick={() => {
          setOpen(open => !open);
        }}
      >
        <Header className="h3">
          <span />
          {heading}
        </Header>
      </button>
      <Collapsible
        id={`details${guid}`}
        role="region"
        aria-labelledby={`button${guid}`}
        open={open}
        active={focus && open}
        onOpen={() => {
          setOpened(true);
        }}
        onClose={() => {
          setOpen(false);
          if (button.current) {
            button.current.focus();
          }
        }}
        onClosed={() => {
          setOpened(false);
        }}
      >
        <div className="current">
          {current.map(({ id, url, image, name, role }) => (
            <a
              key={id}
              href={url}
              className="actor"
              title={more.replace('{title}', String(name))}
              aria-label={more.replace('{title}', String(name))}
              tabIndex={open ? undefined : -1}
            >
              <div className="image flap-top-left flap-bottom-right">
                <div>
                  <Picture {...image} />
                </div>
              </div>
              <div className="text">
                <h4 className="hd-sm">{name}</h4>
                <div className="body-sm role">{role}</div>
              </div>
            </a>
          ))}
        </div>
        {renderPrevious()}
      </Collapsible>
    </section>
  );
}

export default Actors;
